/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useMemo,
  useState
} from 'react';
import {IconButton} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
import {RoundedChipButton} from '@ion/dls/src/components/buttons/RoundedChipButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {CardLabel} from '@ion/dls/src/components/labels/CardLabel';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {TimeStamp} from '@ion/dls/src/components/time/TimeStamp';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {StatusIcon} from '@ion/dls/src/icons/StatusIcon';
import {TrashCanIcon} from '@ion/dls/src/icons/TrashCanIcon';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {dlsAlt, dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import CloseButton from '../components/buttons/CloseButton';
import NotificationsStatusIcon from '../icons/current/NotificationsStatusIcon';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import useThemeContext from '../theme/useThemeContext';
import NotificationCardCTA from './NotificationCardCTA';
import NotificationDetails from './NotificationDetails';

export interface NotificationCardType {
  id?: string | number;
  toastId?: string | number;
  notification?: Notification;
  read?: boolean
  text: string;
  subText?: string;
  type?: string;
  timeStamp?: string;
  viewEventCta?: JSX.Element | null;
  onReviewClick?: (notification: Notification) => void;
  onCardClick?: (notification: Notification, isChecked?: boolean) => void;
  onCloseClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onClearClick?: (notification: Notification) => void;
  onCheckBoxChange?: (checked: boolean, notification: Notification) => void;
  onHover?: (notification: Notification) => void;
  onBlur?: (notification: Notification) => void;
  isToast?: boolean;
  shadowOnHover?: boolean;
  isUrgent?: boolean;
  multiSelect?: boolean;
  onMoreClick?: (notification: Notification) => void;
  onLessClick?: (notification: Notification) => void;
  onDoubleClick?: (id: string | number) => void;
  moreInfoLoading?: boolean;
  moreInfoDetails?: JSX.Element | null;
  showMoreInfo?: boolean;
  showCheckBox?: boolean;
  isChecked?: boolean;
  disableSingleClear?: boolean;
  isCheckAll?: boolean;
  rootStyles?: {
    [key: string]: string;
  };
  onToastRemoved?: (id: string | number) => void;
  isHideViewEventCta?: boolean;
  hasAccessToEntiry?: boolean;
};

type StyleProps = {
  isToast: boolean,
  text: string,
  read: boolean,
  isValidEntityCTA: boolean
};

// the toast's close column, and the gap it and the rows keep ...
const TOAST_CLOSE_SIZE = 44;
const TOAST_GAP        = 8;

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          smallExpansion: {
            open: expansionOpen
          }
        }
      }
    }
  } = theme;

  return {
    notificationCard : {
      minHeight                      : '136px',
      maxWidth                       : '414px',
      padding                        : '18px',
      backgroundColor                : dlsColor('white'),
      borderBottom                   : ({isToast}: StyleProps) => isToast ? 'none' : `solid 1px ${dlsColor('grey5')}`,
      [theme.breakpoints.down('sm')] : {
        maxWidth : 'none'
      }
    },
    currentNotificationCard : {
      padding                        : ({isToast}: StyleProps) => isToast ? '16px 0px 16px 16px' : '24px',
      maxWidth                       : '456px',
      position                       : ({isToast}: StyleProps) => isToast ? 'relative' : 'unset',
      minHeight                      : ({isToast}: StyleProps) => isToast ? '102px' : '189px',
      borderTop                      : `1px solid ${dlsColor('grey5')}`,
      borderRight                    : ({isToast}: StyleProps) => isToast ? `1px solid ${dlsColor('grey5')}` : 'none',
      borderBottom                   : ({isToast}: StyleProps) => isToast ? `1px solid ${dlsColor('grey5')}` : 'none',
      borderLeft                     : ({isToast}: StyleProps) => isToast ? `1px solid ${dlsColor('grey5')}` : 'none',
      borderRadius                   : ({isToast}: StyleProps) => isToast ? '12px' : '0',
      boxShadow                      : ({isToast}: StyleProps) => isToast ? dlsShadow(1) : 'none',
      backgroundColor                : dlsColor('white'),
      [theme.breakpoints.down('sm')] : {
        maxWidth : 'none'
      }
    },
    notificationCardSelected : {
      backgroundColor : toggleThemeStyle(`${dlsAlt('components', 'icon', 'selected')} !important`, `${dlsColor('grey8')} !important`)
    },
    hoverEffect : {
      '&:hover' : {
        backgroundColor : toggleThemeStyle(`${dlsAlt('components', 'icon', 'selected')} !important`, `${dlsColor('grey8')} !important`)
      }
    },
    notificationCardHeader : toggleThemeStyle({
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'center',
      marginBottom   : '12px'
    }, {
      display        : 'flex',
      marginTop      : '12px',
      alignItems     : 'center',
      marginLeft     : '14px',
      justifyContent : 'space-between'
    }),
    notificationCardSubHeader : {
      margin : '-15px 0 11px 4px'
    },
    notificationStatusFoundation : {
      display         : 'flex',
      minHeight       : '24px',
      maxHeight       : '24px',
      padding         : '2px',
      width           : 'fit-content',
      backgroundColor : dlsColor('white'),
      boxSizing       : 'border-box',
      outline         : `1px solid ${dlsColor('grey6')}50`,
      borderRadius    : '4px'
    },
    notificationStatusBadge : {
      ...dlsAlt('typography', 'label3'),
      color           : ({badgeTextColor}: {badgeTextColor: string}) => badgeTextColor,
      backgroundColor : ({badgeColor}: {badgeColor: string}) => badgeColor,
      fontWeight      : 500,
      fontSize        : '10px',
      lineHeight      : '20px',
      borderRadius    : '2px',
      padding         : '0px 8px',
      width           : 'fit-content',
      display         : 'flex',
      alignItems      : 'center',
      justifyContent  : 'center'
    },
    notificationCardHeaderRight : toggleThemeStyle({
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      minHeight      : '38px'
    }, {
      gap        : '8px',
      display    : 'flex',
      alignItems : 'center'
    }),
    notificationTimeStamp : toggleThemeStyle({
      paddingLeft : '4px',
      marginRight : ({read}: StyleProps) => read ? '-12px' : 0,
      ...theme.dls?.typography.caption
    }, {
      color         : dlsColor('grey2'),
      fontSize      : '12px',
      fontFamily    : 'Inter',
      fontWeight    : 500,
      lineHeight    : '15px',
      textAlign     : 'right',
      letterSpacing : '0px'
    }),
    notificationCardTextWrapper : {
      margin : '4px 0'
    },
    notificationCardText : toggleThemeStyle({
      minHeight    : '48px',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      ...theme.dls?.typography.body1
    }, {
      top           : '25px',
      color         : dlsColor('grey2'),
      fontSize      : '12px',
      overflow      : 'hidden',
      position      : 'relative',
      fontFamily    : 'Inter',
      fontWeight    : 500,
      lineHeight    : '100%',
      marginLeft    : '14px',
      letterSpacing : '0px',
      textOverflow  : 'ellipsis'
    }),
    notificationReadIcon : {
      opacity    : '0',
      transition : 'opacity 300ms ease-out'
    },
    notificationUnreadIcon : {
      opacity     : '1',
      display     : 'flex',
      alignItems  : 'center',
      marginRight : '5px'
    },
    moreInfoButton : {
      border          : 'none',
      backgroundColor : 'transparent',
      outline         : 'none',
      padding         : '0',
      margin          : '5px 0',
      marginTop       : ({isValidEntityCTA}: StyleProps) => isValidEntityCTA ? '12px' : '-18px',
      textDecoration  : 'underline',
      position        : 'absolute',
      right           : '18px',
      minWidth        : '90px',
      display         : 'flex',
      justifyContent  : 'center',
      alignItems      : 'center',
      ...theme.dls?.typography.body1,
      '&:hover'       : {
        cursor : 'pointer'
      }
    },
    notificationMoreInfo : {
      overflow   : 'hidden',
      transition : `max-height ${expansionOpen}`,
      margin     : '15px 0'
    },
    notificationHoverCta : {
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      marginTop      : '0',
      minWidth       : '50px',
      minHeight      : '30px'
    },
    trashIconHover : {
      display        : 'flex',
      minHeight      : '30px',
      alignItems     : 'center',
      marginTop      : '0',
      justifyContent : 'flex-end'
    },
    moreInfoWrapper : {
      margin     : '15px 0',
      transition : 'max-height 300ms ease-in',
      overflowY  : 'auto',
      overflowX  : 'clip'
    },
    currentMoreInfoTable : {
      '& tbody' : {
        width          : '100%',
        margin         : '8px 0 0',
        borderCollapse : 'collapse'
      },
      '& td' : {
        padding       : '4px 0',
        fontSize      : '12px',
        fontFamily    : 'Inter',
        lineHeight    : '16px',
        verticalAlign : 'top'
      },
      '& td:first-child' : {
        color         : dlsColor('grey3'),
        width         : '30%',
        fontWeight    : 500,
        whiteSpace    : 'nowrap',
        paddingRight  : '16px',
        letterSpacing : '0'
      },
      '& td:last-child' : {
        color         : dlsColor('grey2'),
        fontWeight    : 600,
        letterSpacing : '0.5px'
      }
    },
    moreInfoWrapperHidden : {
      maxHeight  : '0px',
      margin     : toggleThemeStyle('15px 0', '0'),
      overflow   : 'hidden',
      transition : 'max-height 300ms ease-out'
    },
    notificationCheckboxWrapper : {
      maxWidth : '25px'
    },
    notificationCancelClear : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center',
      marginLeft     : '3px',
      cursor         : 'pointer'
    },
    noLeftPadding : toggleThemeStyle({
      marginTop   : ({text}: StyleProps) => !text ? '2px' : '0',
      paddingLeft : '0 !important'
    }, {
      marginTop     : ({text}: StyleProps) => !text ? '2px' : '0',
      paddingLeft   : '0 !important',
      color         : `${dlsColor('grey2')} !important`,
      fontSize      : '12px !important',
      fontFamily    : 'Inter !important',
      fontWeight    : 600,
      lineHeight    : '16px !important',
      letterSpacing : '0.5px !important'
    }),
    ctaContainer : toggleThemeStyle({
      marginLeft : '-4px',
      display    : 'flex',
      alignItems : 'center'
    }, {
      gap        : '8px',
      display    : 'flex',
      flexWrap   : 'wrap',
      alignItems : 'center'
    }),
    viewAssetCta : toggleThemeStyle({
      padding       : '4px 4px',
      fontSize      : '14px',
      textTransform : 'uppercase'
    }, {
      padding       : '4px 4px',
      fontSize      : '13px',
      textTransform : 'capitalize'
    }),
    notificationCardTitleRow : {
      bottom         : '2px',
      display        : 'flex',
      position       : 'relative',
      alignItems     : 'center',
      justifyContent : 'space-between'
    },
    notificationCardTitle : {
      color                 : dlsColor('linkBlue'),
      cursor                : 'pointer',
      fontSize              : '20px',
      fontFamily            : 'Roboto',
      fontWeight            : 500,
      lineHeight            : '22px',
      letterSpacing         : '0px',
      '&:hover, &$selected' : {
        textDecoration  : 'underline',
        backgroundColor : 'transparent'
      },
      '&:focus' : {
        outline : 'none'
      },
      '&:focus-visible' : {
        outline       : `2px solid ${dlsColor('skyBlue')}`,
        borderRadius  : '4px',
        outlineOffset : '2px'
      }
    },
    closeIconWrapper : {
      cursor    : 'pointer',
      marginTop : '-18px'
    },
    notificationCardTimeStamp : {
      display       : 'flex',
      flexDirection : 'column'
    },
    eventContainer : {
      marginRight : '46px'
    },
    // the current theme's toast ("Notification/WDesktop/Pop-Up"): its rows
    // in a column, 16px in and 16px apart, clear of the 44px close column
    // (and the 8px gap before it) on its right ...
    toastCard : {
      '&&' : {
        boxSizing    : 'border-box',
        minHeight    : 124,
        paddingRight : TOAST_CLOSE_SIZE + TOAST_GAP,
        width        : '100%'
      }
    },
    toastHeader : {
      '&&' : {
        alignItems : 'flex-start',
        margin     : 0,
        minHeight  : 32
      }
    },
    toastText : {
      '&&' : {
        marginTop : 16,
        top       : 0
      }
    },
    toastTitleRow : {
      '&&' : {
        bottom    : 0,
        gap       : TOAST_GAP,
        minHeight : 44
      }
    },
    toastEvent : {
      '&&' : {
        marginRight : 0,
        padding     : '0 16px'
      }
    },
    // its own 44px column, the 24px close centred in it, at the top right ...
    toastClose : {
      '&&' : {
        alignItems     : 'center',
        display        : 'flex',
        height         : TOAST_CLOSE_SIZE,
        justifyContent : 'center',
        marginTop      : 0,
        position       : 'absolute',
        right          : 0,
        top            : 0,
        width          : TOAST_CLOSE_SIZE
      }
    }
  };
});

/**
 * @function NotificationCard
 * @returns Node
 *
 * Card that can be used for notifications. isToast boolean prop
 * will hide some info and just show a close button that fires a
 * callback.
 *
 * ```typescript
 * import {NotificationCard} from '@ion/dls/src/notifications'
 *
 * <NotificationCard read
 *   isToast
 *   isUrgent
 *   notification={{...}}
 *   text='Notification Title'
 *   type='Time Related'
 *   timeStamp='Mon Nov 02 2020 21:58:43'
 *   onDebouncedHover={handleHover} />
 *
 * ```
 *
 */
export const NotificationCard = ({
  id,
  text,
  subText,
  type,
  timeStamp,
  viewEventCta,
  onReviewClick,
  onCardClick,
  onCloseClick,
  onClearClick,
  onCheckBoxChange,
  onHover,
  onBlur,
  notification,
  isUrgent,
  onMoreClick,
  onLessClick,
  onDoubleClick,
  moreInfoLoading = false,
  showMoreInfo = false,
  isChecked = false,
  showCheckBox = false,
  isCheckAll = false,
  rootStyles = {},
  read = false,
  isToast = false,
  hasAccessToEntiry = true,
  isHideViewEventCta = false,
  disableSingleClear = false
}: NotificationCardType) => {
  const [showTrashCan, setShowTrashCan]       = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const {isDesktop, isPhone}                  = useMedia();
  const {assetId, device, statusBadge}        = notification || {};

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();
  const isCurrentToast = isCurrentTheme && isToast;

  const isValidEntityCTA = useMemo(() => isDeviceAlert(notification) ? !!device : !!assetId, [notification]);
  const classes          = useStyles({
    isToast,
    isValidEntityCTA,
    read,
    text,
    badgeColor     : statusBadge?.color,
    badgeTextColor : statusBadge?.textColor
  });

  function handleCardClick(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    onCardClick?.(notification, isChecked);
  }

  function handleReviewClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onReviewClick?.(notification);
  }

  function handleCloseClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onCloseClick?.(e);
  }

  function handleMoreClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onMoreClick?.(notification);
  }

  function handleLessClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onLessClick?.(notification);
  }

  function handleMouseEnter(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    if (!isToast) {
      if (!isPhone) {
        setShowTrashCan(true);
      }
      onHover?.(notification);
    }
  }

  function handleDoubleClick(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    onDoubleClick?.(id);
  }

  function handleMouseLeave() {
    if (!isToast) {
      if (!isPhone) {
        setShowTrashCan(false);
      }
      onBlur?.(notification);
    }
  }

  function handleTrashHover(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    setShowClearButton(true);
  }

  function handleTrashBlur() {
    setShowClearButton(false);
  }

  function handleClearClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onClearClick?.(notification);
    setShowClearButton(false);
  }

  function handleMobileTrashClick(e: MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    setShowClearButton(true);
  }

  function handleCheckBoxClick(e: ChangeEvent<HTMLInputElement>) {
    e.stopPropagation();
    onCheckBoxChange?.(e.target.checked, notification);
    onCardClick?.(notification, isChecked);
  }

  function handleMobileClearCancel(e: MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    setShowClearButton(false);
  }

  const labelTextColor = (
    isCurrentTheme
      ? (isUrgent ? dlsColor('paleRed3') : dlsColor('grey2'))
      : (isUrgent ? dlsColor('white') : dlsColor('grey1'))
  );

  const labelBgColor = (
    isCurrentTheme
      ? (isUrgent ? '#E2231A1A' : '#8282821A')
      : (isUrgent ? dlsColor('caRed') : dlsColor('grey6'))
  );

  return (
    <div className={classnames(isCurrentTheme ? classes.currentNotificationCard : classes.notificationCard, {
      [classes.notificationCardSelected] : isChecked,
      [classes.hoverEffect]              : isDesktop,
      [classes.toastCard]                : isCurrentToast
    })}
    onClick={handleCardClick}
    onDoubleClick={handleDoubleClick}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    {...rootStyles}>
      <div className={classnames(classes.notificationCardHeader, {[classes.toastHeader]: isCurrentToast})}>
        {
          statusBadge ? (
            <div className={classes.notificationStatusFoundation}>
              <div className={classes.notificationStatusBadge}>
                {statusBadge.label}
              </div>
            </div>
          ) : (
            <CardLabel textColor={labelTextColor}
              color={labelBgColor}
              labelText={type}
              letterSpacing={isCurrentTheme && isToast ? '1px' : '0.5px'} />
          )
        }
        <div className={classes.notificationCardHeaderRight}>
          <span className={classes.notificationTimeStamp}>
            <TimeStamp time={timeStamp}
              daysAgo={!isCurrentTheme}
              notificationCardTimeStamp={classes.notificationCardTimeStamp} />
          </span>
          <Render.If condition={!isToast}>
            <span className={
              read ? classes.notificationReadIcon : classes.notificationUnreadIcon
            }>
              <Render>
                <Render.If condition={isCurrentTheme}>
                  <NotificationsStatusIcon />
                </Render.If>
                <Render.Else>
                  <StatusIcon type={dlsColor('caBlue')} />
                </Render.Else>
              </Render>
            </span>
          </Render.If>
          {
            <Render>
              <Render.If condition={isToast}>
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <div className={classnames(classes.closeIconWrapper, {[classes.toastClose]: isCurrentToast})}>
                      <CloseButton onClick={handleCloseClick} />
                    </div>
                  </Render.If>
                  <Render.Else>
                    <IconButton size='small'
                      onClick={handleCloseClick}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </Render.Else>
                </Render>
              </Render.If>
              <Render.Else>
                <>
                  {
                    <Render>
                      <Render.If condition={isChecked}>
                        <div className={classes.notificationCheckboxWrapper}>
                          <CheckBox onChange={handleCheckBoxClick}
                            checked={isChecked} />
                        </div>
                      </Render.If>
                      <Render.Else>
                        <>
                          {
                            <Render.If condition={!disableSingleClear && showTrashCan && !isCurrentTheme}>
                              <div onMouseEnter={handleTrashHover} onMouseLeave={handleTrashBlur}
                                className={showClearButton ? (classes.notificationHoverCta)
                                  : (classes.trashIconHover)}>
                                <Hidden hide='mobile'>
                                  {
                                    <Render>
                                      <Render.If condition={showClearButton}>
                                        <RoundedChipButton onClick={handleClearClick}
                                          labelText={translate('notifications:panel.clear')} />
                                      </Render.If>
                                      <Render.Else>
                                        <TrashCanIcon />
                                      </Render.Else>
                                    </Render>
                                  }
                                </Hidden>
                              </div>
                            </Render.If>
                          }
                        </>
                      </Render.Else>
                    </Render>
                  }
                  <Hidden hide='desktop'>
                    {
                      <Render>
                        <Render.If condition={!disableSingleClear && !showClearButton}>
                          <div onClick={handleMobileTrashClick}><TrashCanIcon /></div>
                        </Render.If>
                        <Render.Else>
                          <Render.If condition={showClearButton}>
                            <>
                              <RoundedChipButton onClick={handleClearClick}
                                labelText='Clear' />
                              <span className={classes.notificationCancelClear}
                                onClick={handleMobileClearCancel}>
                                <MaterialExitCloseIcon size={IconSize.small} />
                              </span>
                            </>
                          </Render.If>
                        </Render.Else>
                      </Render>
                    }
                  </Hidden>
                </>
              </Render.Else>
            </Render>
          }
        </div>
      </div>
      <div className={classnames(classes.notificationCardText, {[classes.toastText]: isCurrentToast})}>
        <Render.If condition={!isCurrentTheme}>
          <HeaderTypography variant='h4'>{text}</HeaderTypography>
        </Render.If>
        <Render.If condition={isCurrentTheme}>
          <div className={classnames(classes.notificationCardTitleRow, {[classes.toastTitleRow]: isCurrentToast})}>
            <Render.If condition={text}>
              <span className={classes.notificationCardTitle} onClick={handleReviewClick}>{text}</span>
            </Render.If>
            <Render.If condition={isToast}>
              <span className={classnames(classes.eventContainer, {[classes.toastEvent]: isCurrentToast})}>
                {!isHideViewEventCta && viewEventCta}
              </span>
            </Render.If>
          </div>
        </Render.If>
        <Render.If condition={subText && !isToast}>
          <BodyTypography className={classes.noLeftPadding}>{subText}</BodyTypography>
        </Render.If>
        {
          <Render.If condition={!isToast}>
            <div className={classnames(
              showMoreInfo && !moreInfoLoading
                ? classes.moreInfoWrapper
                : classes.moreInfoWrapperHidden,
              {[classes.currentMoreInfoTable]: isCurrentTheme}
            )}>
              <Render.If condition={!moreInfoLoading && notification?.details}>
                <NotificationDetails notification={notification} />
              </Render.If>
            </div>
          </Render.If>
        }
      </div>
      {
        <Render.If condition={!isToast && !isCurrentTheme && !statusBadge}>
          <>
            <button onClick={showMoreInfo ? handleLessClick : handleMoreClick}
              disabled={moreInfoLoading}
              className={classes.moreInfoButton}>
              {
                <Render>
                  <Render.If condition={moreInfoLoading}>
                    <IonSpinner width='20px' height='20px' />
                  </Render.If>
                  <Render.Else>
                    {translate(showMoreInfo ? 'notifications:panel.readLess' : 'notifications:panel.readMore')}
                  </Render.Else>
                </Render>
              }
            </button>
          </>
        </Render.If>
      }
      <Render.If condition={!isCurrentTheme}>
        <div className={classes.ctaContainer}>
          <Render.If condition={hasAccessToEntiry && isValidEntityCTA}>
            <TertiaryButton className={classes.viewAssetCta} onClick={handleReviewClick}>
              {translate(`notifications:panel.${isDeviceAlert(notification) ? 'viewDevice' : 'viewAsset'}`)}
            </TertiaryButton>
          </Render.If>
          {!isHideViewEventCta && viewEventCta}
        </div>
      </Render.If>
      <Render.If condition={isCurrentTheme && !isToast}>
        <NotificationCardCTA viewEventCta={viewEventCta}
          handleLessClick={handleLessClick}
          handleMoreClick={handleMoreClick}
          handleClearClick={handleClearClick}
          notificationHoverCta={classes.notificationHoverCta}
          isHideViewEventCta={isHideViewEventCta}
          moreInfoLoading={moreInfoLoading}
          showMoreInfo={showMoreInfo} />
      </Render.If>
    </div>
  );
};

export default NotificationCard;
